import {
  add,
  endOfMonth,
  format,
  startOfISOWeek,
  startOfMonth,
} from 'date-fns';

type DateRange =
  | '当日'
  | '前日'
  | '今週'
  | '前週'
  | '前週同曜日'
  | '今月'
  | '前月'
  | '前年同月'
  | '過去7日'
  | '過去14日'
  | '過去28日'
  | '過去60日'
  | '過去xx日'
  | '平日/土日祝比較'
  | 'カスタム';

const MIN_DAYS = 1;
const DEFAULT_DAYS = 1;

const dateRangeToStartEndDate = (
  dateRange: DateRange,
  baseDate: Date,
  /**
   * 過去xx日のカスタム日数
   */
  customDate?: string
): { startDate: Date; endDate: Date } => {
  switch (dateRange) {
    case '当日': {
      return {
        startDate: baseDate,
        endDate: baseDate,
      };
    }
    case '前日': {
      return {
        startDate: add(baseDate, { days: -1 }),
        endDate: add(baseDate, { days: -1 }),
      };
    }
    case '今週': {
      const startDate = startOfISOWeek(baseDate);
      return {
        startDate,
        endDate: baseDate,
      };
    }
    case '前週': {
      const lastWeek = add(baseDate, { weeks: -1 });
      const startDate = startOfISOWeek(lastWeek);
      return {
        startDate,
        endDate: add(startDate, { days: 6 }),
      };
    }
    case '前週同曜日': {
      const aWeekAgo = add(baseDate, { weeks: -1 });
      return {
        startDate: aWeekAgo,
        endDate: aWeekAgo,
      };
    }
    case '今月': {
      return {
        startDate: startOfMonth(baseDate),
        endDate: baseDate,
      };
    }
    case '前月': {
      const lastMonth = add(baseDate, { months: -1 });
      return {
        startDate: startOfMonth(lastMonth),
        endDate: endOfMonth(lastMonth),
      };
    }
    case '前年同月': {
      const lastMonth = add(baseDate, { years: -1 });
      return {
        startDate: startOfMonth(lastMonth),
        endDate: endOfMonth(lastMonth),
      };
    }
    case '過去7日': {
      return {
        startDate: add(baseDate, { days: -7 }),
        endDate: add(baseDate, { days: -1 }),
      };
    }
    case '過去14日': {
      return {
        startDate: add(baseDate, { days: -14 }),
        endDate: add(baseDate, { days: -1 }),
      };
    }
    case '過去28日': {
      return {
        startDate: add(baseDate, { days: -28 }),
        endDate: add(baseDate, { days: -1 }),
      };
    }
    case '過去60日': {
      return {
        startDate: add(baseDate, { days: -60 }),
        endDate: add(baseDate, { days: -1 }),
      };
    }
    case '過去xx日': {
      const n = parseInt(customDate ?? '1', 10);
      const days = Number.isNaN(n) || n < MIN_DAYS ? DEFAULT_DAYS : n;
      // 一律: 基準日から見て過去N日、最終日は基準日の前日
      return {
        startDate: add(baseDate, { days: -days }),
        endDate: add(baseDate, { days: -1 }),
      };
    }
    case '平日/土日祝比較': {
      return {
        startDate: baseDate,
        endDate: baseDate,
      };
    }
    default: {
      // 想定外の入力に対する安全ガード
      return {
        startDate: baseDate,
        endDate: baseDate,
      };
    }
  }
};

/**
 * dateRangeからstart/end算出する
 *
 * 比較期間の算出はendDateをbaseDateに指定してください
 */
export const dateRangeToStartEnd = (
  dateRange: DateRange,
  baseDate = new Date(),
  customDate?: string
): { startDate: string; endDate: string } => {
  const result = dateRangeToStartEndDate(dateRange, baseDate, customDate);
  // ガード: 未定義ならば本日を返す（安全ネット）
  const startDate = result?.startDate ?? baseDate;
  const endDate = result?.endDate ?? baseDate;
  return {
    startDate: format(startDate, 'yyyy-MM-dd'),
    endDate: format(endDate, 'yyyy-MM-dd'),
  };
};
